import { type ReactNode, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Alert, Box, Button, Chip, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, MenuItem, Paper,
  Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel, TextField, Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useQueryClient } from '@tanstack/react-query';
import { NaglowekEkranu } from '@/components/NaglowekEkranu';
import { EmptyState, ErrorState, LoadingState } from '@/components/states';
import type {
  KontoUzytkownika, RolaKonta, SortowanieKont, UzytkownicyOdpowiedz, ZapytanieUzytkownikow,
} from '@/lib/api/analytics';
import {
  KWOTA_DO_SPRAWDZENIA_GROSZE, OSTRZEZENIE_WYSOKIEJ_KWOTY, kwotaDoGroszy, nowyIdentyfikatorOperacji, opisBleduOperacji,
  powodBleduOdczytu, type OpisBledu,
} from '@/lib/administracja/komunikaty';
import { useAuth } from '@/lib/auth/AuthContext';
import { formatCount, formatGrosze } from '@/lib/format';
import { useAnalyticsApi, useAudytUzytkownikow, useUzytkownicy } from '@/lib/useAnalytics';

/*
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * ADMINISTRACJA → UŻYTKOWNICY (OWNER 2026-10-10)
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * Wyrocznia: stary panel admina → „Konta" (/edit-users). Te same kolumny, te same filtry (rola,
 * imię, e-mail, miasto, szkoła, potwierdzony) i ten sam nagłówek („Ilość klientów", „Suma portfeli"
 * z CAŁEGO zbioru), plus jedno szybkie pole wyszukiwania. Filtry liczy serwer CC na PEŁNEJ liście —
 * strona nigdy nie jest podstawą wyniku ani sum.
 *
 * Zapisy (doładowanie konta klienta; usunięcie konta klienta ALBO restauratora — decyzja OWNERA D-ADM-1)
 * idą tymi samymi operacjami systemu głównego co w starym panelu, z hasłem operatora. Usuwane jest
 * wyłącznie KONTO — restauracja jako obiekt zostaje. Doładowanie bez własnego limitu CC (D-ADM-2),
 * od 500 zł tylko ostrzeżenie. Saldo PO operacji to odczyt systemu głównego, nie „przed + kwota".
 * Hasło żyje wyłącznie w stanie okna i znika przy jego zamknięciu.
 *
 * Czego tu NIE ma (zostaje w starym panelu, poza zakresem pakietu 2026-10-10): blokada konta,
 * ponowne wysłanie kodu weryfikacyjnego, „Dodaj restaurację", eksport CSV.
 */

const ROZMIARY = [10, 25, 50, 100] as const;
const FILTRY_TEKSTOWE = ['q', 'imie', 'email', 'telefon'] as const;
const FILTRY_WYBORU = ['rola', 'miasto', 'szkola', 'potwierdzony', 'zablokowany'] as const;

const KOLUMNY: readonly { klucz: string; etykieta: string; sort?: SortowanieKont; align?: 'right' | 'center' }[] = [
  { klucz: 'rola', etykieta: 'Rola', sort: 'rola' },
  { klucz: 'imie', etykieta: 'Imię', sort: 'imie' },
  { klucz: 'utworzono', etykieta: 'Data rejestracji', sort: 'utworzono' },
  { klucz: 'email', etykieta: 'E-mail', sort: 'email' },
  { klucz: 'telefon', etykieta: 'Numer' },
  { klucz: 'miasto', etykieta: 'Miasto', sort: 'miasto' },
  { klucz: 'szkola', etykieta: 'Szkoła', sort: 'szkola' },
  { klucz: 'transakcje', etykieta: 'Transakcje', sort: 'transakcje', align: 'right' },
  { klucz: 'saldo', etykieta: 'Stan portfela', sort: 'saldo', align: 'right' },
  { klucz: 'waznosc', etykieta: 'Ważność środków' },
  { klucz: 'potwierdzony', etykieta: 'Potwierdzony', sort: 'potwierdzony', align: 'center' },
  { klucz: 'zablokowany', etykieta: 'Zablokowany', sort: 'zablokowany', align: 'center' },
  { klucz: 'akcje', etykieta: 'Akcje', align: 'right' },
];

const dataPl = (iso: string | null) => (iso
  ? new Date(iso).toLocaleDateString('pl-PL', { timeZone: 'Europe/Warsaw' })
  : '—');

type Frazy = Partial<Record<(typeof FILTRY_TEKSTOWE)[number], string>>;

/** Frazy osobowe zastane w adresie (stary link) — przenoszone do stanu ekranu i usuwane z adresu. */
function frazyZAdresu(p: URLSearchParams): Frazy {
  const f: Frazy = {};
  for (const k of FILTRY_TEKSTOWE) {
    const v = p.get(k);
    if (v) f[k] = v;
  }
  return f;
}

/**
 * Zapytanie do serwera: filtry BEZ danych osobowych z adresu (przeżywają odświeżenie i dają się podesłać
 * linkiem), frazy osobowe (imię, e-mail, telefon, szybkie wyszukiwanie) — wyłącznie ze stanu ekranu.
 * Te nigdy nie trafiają do adresu: adres ląduje w historii przeglądarki i w dziennikach serwerów.
 */
function zapytanieZAdresu(p: URLSearchParams, frazy: Frazy): ZapytanieUzytkownikow {
  const q: ZapytanieUzytkownikow = {};
  for (const k of FILTRY_WYBORU) {
    const v = p.get(k);
    if (v) (q as Record<string, string>)[k] = v;
  }
  for (const k of FILTRY_TEKSTOWE) {
    const v = frazy[k]?.trim();
    if (v) q[k] = v;
  }
  const sort = p.get('sort');
  if (sort) q.sort = sort as SortowanieKont;
  if (p.get('kierunek') === 'rosnaco') q.kierunek = 'rosnaco';
  const strona = Number(p.get('strona'));
  if (Number.isInteger(strona) && strona > 1) q.strona = strona;
  const rozmiar = Number(p.get('rozmiar'));
  q.rozmiar = (ROZMIARY as readonly number[]).includes(rozmiar) ? rozmiar : 25;
  return q;
}

function wartoscKomorki(k: KontoUzytkownika, klucz: string) {
  switch (klucz) {
    case 'rola': return k.rola ?? '—';
    case 'imie': return k.imie ?? '—';
    case 'utworzono': return dataPl(k.utworzono);
    case 'email': return k.email ?? '—';
    case 'telefon': return k.telefon ?? '—';
    case 'miasto': return k.miasto ?? '—';
    case 'szkola': return k.szkola ?? '—';
    case 'transakcje': return `${formatGrosze(k.transakcjeGrosze)} (${formatCount(k.transakcjeLiczba)})`;
    case 'saldo': return formatGrosze(k.saldoGrosze);
    case 'waznosc': return dataPl(k.waznoscSrodkow);
    case 'potwierdzony': return k.potwierdzony ? 'tak' : 'nie';
    case 'zablokowany': return k.zablokowany ? 'tak' : 'nie';
    default: return null;
  }
}

/** Typ konta w oknach operacji — wielkimi literami, żeby nie dało się go przeoczyć. */
const typKonta = (k: KontoUzytkownika) => (k.rola === 'Restaurator' ? 'RESTAURATOR' : k.rola === 'Klient' ? 'KLIENT' : (k.rola ?? '—').toUpperCase());

/** Lista „etykieta — wartość" (dl) w oknach operacji. */
function Dane({ wiersze, etykieta }: { wiersze: [string, ReactNode][]; etykieta: string }) {
  return (
    <Box component="dl" sx={{ display: 'grid', gridTemplateColumns: 'max-content 1fr', columnGap: 2, rowGap: 0.5, m: 0 }} aria-label={etykieta}>
      {wiersze.map(([k, v]) => (
        <Box key={k} sx={{ display: 'contents' }}>
          <Typography component="dt" variant="body2" sx={{ color: 'text.secondary' }}>{k}</Typography>
          <Typography component="dd" variant="body2" sx={{ m: 0, fontWeight: 600, wordBreak: 'break-all' }} data-pole={k}>{v}</Typography>
        </Box>
      ))}
    </Box>
  );
}

/** Identyfikacja konta w oknach operacji — operator ma widzieć, KOGO dotyczy zapis i JAKIEGO TYPU jest to konto. */
function Identyfikacja({ konto }: { konto: KontoUzytkownika }) {
  return (
    <Dane
      etykieta="Konto, którego dotyczy operacja"
      wiersze={[
        ['Typ konta', <Chip key="typ" size="small" color={konto.rola === 'Restaurator' ? 'warning' : 'primary'} label={typKonta(konto)} data-typ-konta={typKonta(konto)} />],
        ['Imię', konto.imie ?? '—'],
        ['E-mail', konto.email ?? '—'],
        ['Telefon', konto.telefon ?? '—'],
        ['Szkoła', konto.szkola ?? '—'],
        ['Miasto', konto.miasto ?? '—'],
        ['Saldo teraz', konto.rola === 'Klient' ? formatGrosze(konto.saldoGrosze) : '— (konto bez portfela)'],
        ['ID konta', konto.id],
      ]}
    />
  );
}

// ── DOŁADOWANIE ─────────────────────────────────────────────────────────────────────────────────

function OknoDoladowania({ konto, loginDomyslny, onZamknij, onPoZapisie }: {
  konto: KontoUzytkownika; loginDomyslny: string; onZamknij: () => void; onPoZapisie: () => Promise<unknown>;
}) {
  const api = useAnalyticsApi();
  const [kwota, setKwota] = useState('');
  const [login, setLogin] = useState(loginDomyslny);
  const [haslo, setHaslo] = useState('');
  const [krok, setKrok] = useState<'formularz' | 'potwierdzenie'>('formularz');
  const [wysylanie, setWysylanie] = useState(false);
  const [blad, setBlad] = useState<OpisBledu | null>(null);
  const [wynik, setWynik] = useState<{ przed: number; po: number | null; kwota: number } | null>(null);
  // Klucz idempotencji: JEDEN na otwarcie okna. Ponowienie po błędzie albo przekroczeniu czasu niesie
  // ten sam — system główny nie zaksięguje drugi raz.
  const [operacjaId] = useState(nowyIdentyfikatorOperacji);

  const grosze = kwotaDoGroszy(kwota);
  const gotowe = grosze !== null && haslo.length > 0 && login.trim().length > 0;
  // Ostrzeżenie, nie blokada (D-ADM-2): ani CC, ani system główny nie mają osobnego limitu doładowania.
  const wysoka = grosze !== null && grosze >= KWOTA_DO_SPRAWDZENIA_GROSZE;

  function zamknij() {
    setHaslo('');
    onZamknij();
  }

  async function wyslij() {
    if (!gotowe || wysylanie) return;
    setWysylanie(true);
    setBlad(null);
    try {
      const r = await api.uzytkownicyDoladuj({ uzytkownikId: konto.id, kwota, operacjaId, haslo, loginOperatora: login.trim() });
      setHaslo('');
      setWynik({ przed: r.saldoPrzedGrosze, po: r.saldoPoGrosze, kwota: r.kwotaGrosze });
    } catch (e) {
      const opis = opisBleduOperacji(e);
      setBlad(opis);
      setKrok('formularz');
    } finally {
      setWysylanie(false);
      // Lista zawsze z systemu głównego — także po błędzie (wynik mógł być nieznany).
      await onPoZapisie();
    }
  }

  return (
    <Dialog open onClose={() => (wysylanie ? undefined : zamknij())} fullWidth maxWidth="sm" aria-labelledby="tytul-doladowania">
      <DialogTitle id="tytul-doladowania">Doładowanie konta klienta</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Identyfikacja konto={konto} />
          {wynik ? (
            <Alert severity="success" role="status">
              Doładowano {formatGrosze(wynik.kwota)}. Saldo przed: {formatGrosze(wynik.przed)} → po (odczyt systemu głównego):{' '}
              <strong>{wynik.po === null ? 'konta nie ma już na liście' : formatGrosze(wynik.po)}</strong>.
            </Alert>
          ) : krok === 'formularz' ? (
            <>
              <TextField
                label="Kwota doładowania (zł)"
                value={kwota}
                onChange={(e) => setKwota(e.target.value)}
                slotProps={{ htmlInput: { inputMode: 'decimal', autoComplete: 'off' } }}
                error={kwota !== '' && grosze === null}
                helperText={kwota !== '' && grosze === null
                  ? 'Liczba złotych większa od zera, najwyżej dwa miejsca po przecinku (np. 25,50), w zakresie systemu głównego (do 21 474 836,47 zł).'
                  : grosze !== null ? `Saldo po doładowaniu (podgląd): ${formatGrosze(konto.saldoGrosze + grosze)}` : ' '}
                autoFocus
              />
              {wysoka ? <Alert severity="warning" data-ostrzezenie="wysoka-kwota">{OSTRZEZENIE_WYSOKIEJ_KWOTY}</Alert> : null}
              <TextField
                label="Login operatora panelu admina"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                slotProps={{ htmlInput: { autoComplete: 'off' } }}
                helperText="Administrator systemu głównego, którego hasło potwierdza operację."
              />
              <TextField
                label="Hasło operatora"
                type="password"
                value={haslo}
                onChange={(e) => setHaslo(e.target.value)}
                slotProps={{ htmlInput: { autoComplete: 'off' } }}
              />
            </>
          ) : (
            <Box data-krok="potwierdzenie" role="status">
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Sprawdź przed potwierdzeniem:</Typography>
              <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Dane
                  etykieta="Podsumowanie doładowania"
                  wiersze={[
                    ['Użytkownik', [konto.imie, konto.email ?? konto.id].filter(Boolean).join(' · ')],
                    ['Typ konta', typKonta(konto)],
                    ['Saldo teraz', formatGrosze(konto.saldoGrosze)],
                    ['Doładowanie', formatGrosze(grosze)],
                    ['Saldo po', `${formatGrosze(konto.saldoGrosze + (grosze ?? 0))} (podgląd; ostateczne saldo poda system główny)`],
                  ]}
                />
              </Paper>
              {wysoka ? <Alert severity="warning" sx={{ mt: 1.5 }} data-ostrzezenie="wysoka-kwota">{OSTRZEZENIE_WYSOKIEJ_KWOTY}</Alert> : null}
              <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 1 }}>
                Operację potwierdza hasło operatora {login.trim()} — ta sama reautoryzacja co w starym panelu admina.
              </Typography>
            </Box>
          )}
          {blad ? (
            <Alert severity={blad.wynikNieznany ? 'warning' : 'error'} role="alert">
              {blad.tekst}
              {blad.wynikNieznany ? ' Ponowienie z tego okna nie zaksięguje drugi raz (ten sam identyfikator operacji).' : ''}
              {blad.korelacja ? <Box component="div" sx={{ mt: 0.5, fontSize: 12 }}>Identyfikator zgłoszenia: {blad.korelacja}</Box> : null}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        {wynik ? (
          <Button variant="contained" onClick={zamknij}>Zamknij</Button>
        ) : krok === 'formularz' ? (
          <>
            <Button onClick={zamknij} disabled={wysylanie}>Anuluj</Button>
            <Button variant="contained" onClick={() => setKrok('potwierdzenie')} disabled={!gotowe || wysylanie}>Dalej</Button>
          </>
        ) : (
          <>
            <Button onClick={() => setKrok('formularz')} disabled={wysylanie}>Wstecz</Button>
            <Button variant="contained" onClick={() => void wyslij()} disabled={!gotowe || wysylanie}>
              {wysylanie ? 'Doładowywanie…' : 'Potwierdź doładowanie'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── USUNIĘCIE KONTA ─────────────────────────────────────────────────────────────────────────────

const porownywalny = (t: string | null) => (t ?? '').trim().toLocaleLowerCase('pl-PL');

function OknoUsuniecia({ konto, loginDomyslny, onZamknij, onPoZapisie }: {
  konto: KontoUzytkownika; loginDomyslny: string; onZamknij: () => void; onPoZapisie: () => Promise<unknown>;
}) {
  const api = useAnalyticsApi();
  const [potwierdzenie, setPotwierdzenie] = useState('');
  const [login, setLogin] = useState(loginDomyslny);
  const [haslo, setHaslo] = useState('');
  const [wysylanie, setWysylanie] = useState(false);
  const [blad, setBlad] = useState<OpisBledu | null>(null);
  const [usuniete, setUsuniete] = useState<boolean | null>(null);

  // Usuwa się konto klienta albo restauratora (D-ADM-1); typ idzie do CC i do systemu głównego, który go sprawdza.
  const restaurator = konto.rola === 'Restaurator';
  const typ: 'Klient' | 'Restaurator' = restaurator ? 'Restaurator' : 'Klient';
  const zgodne = konto.email !== null && porownywalny(potwierdzenie) === porownywalny(konto.email);
  const gotowe = zgodne && haslo.length > 0 && login.trim().length > 0;

  function zamknij() {
    setHaslo('');
    onZamknij();
  }

  async function wyslij() {
    if (!gotowe || wysylanie) return;
    setWysylanie(true);
    setBlad(null);
    try {
      const r = await api.uzytkownicyUsun({ uzytkownikId: konto.id, typKonta: typ, potwierdzenie, haslo, loginOperatora: login.trim() });
      setHaslo('');
      setUsuniete(r.usuniete);
    } catch (e) {
      setBlad(opisBleduOperacji(e));
    } finally {
      setWysylanie(false);
      await onPoZapisie();
    }
  }

  return (
    <Dialog open onClose={() => (wysylanie ? undefined : zamknij())} fullWidth maxWidth="sm" aria-labelledby="tytul-usuniecia">
      <DialogTitle id="tytul-usuniecia">Usunięcie konta — {typKonta(konto)}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {usuniete === null ? (
            <Alert severity="error" role="alert">
              Tej operacji nie da się cofnąć z Control Center. System główny usuwa konto tą samą operacją co „Usuń"
              w starym panelu (konto znika z listy, dane logowania i osobowe są anonimizowane).
            </Alert>
          ) : null}
          {restaurator && usuniete === null ? (
            <Alert severity="warning" data-zakres-usuniecia="tylko-konto">
              Usuwasz wyłącznie <strong>KONTO logowania restauratora</strong> ({konto.email ?? konto.id}). Restauracja
              {konto.miasto ? ` (${konto.miasto})` : ''} — jej menu, szkoły, rozliczenia i pozostałe konta — <strong>NIE</strong> jest
              usuwana.
            </Alert>
          ) : null}
          <Identyfikacja konto={konto} />
          {!restaurator && konto.saldoGrosze > 0 && usuniete === null ? (
            <Alert severity="warning">
              Na koncie jest {formatGrosze(konto.saldoGrosze)}. Control Center nie zwraca środków — upewnij się, że usunięcie jest zamierzone.
            </Alert>
          ) : null}
          {usuniete === true ? (
            <Alert severity="success" role="status">Konto usunięte — nie ma go już na liście systemu głównego.</Alert>
          ) : usuniete === false ? (
            <Alert severity="warning" role="status">
              System główny przyjął operację, ale konto nadal jest na liście. Sprawdź je w systemie głównym, zanim spróbujesz ponownie.
            </Alert>
          ) : (
            <>
              <TextField
                label="Przepisz adres e-mail konta, aby potwierdzić"
                value={potwierdzenie}
                onChange={(e) => setPotwierdzenie(e.target.value)}
                slotProps={{ htmlInput: { autoComplete: 'off', spellCheck: false } }}
                error={potwierdzenie !== '' && !zgodne}
                helperText={konto.email ? (zgodne ? 'Zgodny.' : `Wpisz: ${konto.email}`) : 'Konto bez adresu e-mail — usunięcie tylko w systemie głównym.'}
              />
              <TextField
                label="Login operatora panelu admina"
                value={login}
                onChange={(e) => setLogin(e.target.value)}
                slotProps={{ htmlInput: { autoComplete: 'off' } }}
              />
              <TextField
                label="Hasło operatora"
                type="password"
                value={haslo}
                onChange={(e) => setHaslo(e.target.value)}
                slotProps={{ htmlInput: { autoComplete: 'off' } }}
              />
            </>
          )}
          {blad ? (
            <Alert severity={blad.wynikNieznany ? 'warning' : 'error'} role="alert">
              {blad.tekst}
              {blad.korelacja ? <Box component="div" sx={{ mt: 0.5, fontSize: 12 }}>Identyfikator zgłoszenia: {blad.korelacja}</Box> : null}
            </Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        {usuniete !== null ? (
          <Button variant="contained" onClick={zamknij}>Zamknij</Button>
        ) : (
          <>
            <Button onClick={zamknij} disabled={wysylanie}>Anuluj</Button>
            <Button variant="contained" color="error" onClick={() => void wyslij()} disabled={!gotowe || wysylanie}>
              {wysylanie ? 'Usuwanie…' : restaurator ? 'Usuń konto restauratora' : 'Usuń konto klienta'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
}

// ── EKRAN ───────────────────────────────────────────────────────────────────────────────────────

export function UzytkownicyPage() {
  const auth = useAuth();
  const api = useAnalyticsApi();
  const queryClient = useQueryClient();
  const [odswiezanie, setOdswiezanie] = useState(false);
  const [parametry, setParametry] = useSearchParams();
  // Frazy osobowe (imię, e-mail, telefon, szybkie wyszukiwanie) żyją w STANIE ekranu, nigdy w adresie.
  // Stary link z frazą w adresie: fraza przejęta tutaj, a z adresu usunięta (efekt niżej).
  const [frazy, setFrazy] = useState<Frazy>(() => frazyZAdresu(parametry));
  const zapytanie = useMemo(() => zapytanieZAdresu(parametry, frazy), [parametry, frazy]);
  const lista = useUzytkownicy(zapytanie);
  const [szukaj, setSzukaj] = useState(() => frazy.q ?? '');
  const [zaawansowane, setZaawansowane] = useState(() => FILTRY_WYBORU.some((k) => parametry.get(k))
    || (['imie', 'email', 'telefon'] as const).some((k) => frazy[k]));
  const [doladowanie, setDoladowanie] = useState<KontoUzytkownika | null>(null);
  const [usuniecie, setUsuniecie] = useState<KontoUzytkownika | null>(null);
  const [pokazAudyt, setPokazAudyt] = useState(false);
  const audyt = useAudytUzytkownikow(pokazAudyt);

  const mozeDoladowac = auth.has('users.wallet.write');
  const mozeUsunac = auth.has('users.delete');
  const loginOperatora = auth.realms.cc?.identity?.subject ?? '';

  /** Zmiana parametrów adresu; każda zmiana filtra wraca na pierwszą stronę. */
  function ustaw(zmiany: Record<string, string | null>, zostawStrone = false) {
    setParametry((p) => {
      const n = new URLSearchParams(p);
      for (const [k, v] of Object.entries(zmiany)) {
        if (v === null || v === '') n.delete(k);
        else n.set(k, v);
      }
      if (!zostawStrone) n.delete('strona');
      return n;
    }, { replace: true });
  }

  /** Fraza osobowa — tylko stan ekranu; zmiana filtra wraca na pierwszą stronę (adres bez frazy). */
  function ustawFraze(k: (typeof FILTRY_TEKSTOWE)[number], v: string) {
    setFrazy((f) => ({ ...f, [k]: v.trim() || undefined }));
    ustaw({});
  }

  // Stary link z frazą osobową w adresie: przejęta do stanu przy wejściu — tu znika z adresu (raz).
  useEffect(() => {
    if (FILTRY_TEKSTOWE.some((k) => parametry.get(k))) ustaw(Object.fromEntries(FILTRY_TEKSTOWE.map((k) => [k, null])), true);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- jednorazowo przy wejściu na ekran
  }, []);

  // Szybkie wyszukiwanie: 300 ms po ostatnim znaku — bez zapytania na każdy klawisz.
  useEffect(() => {
    if ((frazy.q ?? '') === szukaj.trim()) return undefined;
    const t = setTimeout(() => ustawFraze('q', szukaj), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `ustawFraze` zmienia się z każdym renderem; zależymy od tekstu
  }, [szukaj]);

  const filtrAktywny = FILTRY_WYBORU.some((k) => parametry.get(k)) || FILTRY_TEKSTOWE.some((k) => frazy[k]);

  function wyczysc() {
    setSzukaj('');
    setFrazy({});
    ustaw(Object.fromEntries(FILTRY_WYBORU.map((k) => [k, null])));
  }

  async function poZapisie() {
    await queryClient.invalidateQueries({ queryKey: ['admin-uzytkownicy'] });
    await queryClient.invalidateQueries({ queryKey: ['admin-uzytkownicy-audyt'] });
  }

  async function odswiez() {
    // Wymuszony odczyt pełnej listy z systemu głównego (serwer CC trzyma ją 60 s); potem bieżąca strona
    // czyta się już ze świeżej listy. Błąd pokaże ponowny odczyt — nie zgadujemy go tutaj.
    setOdswiezanie(true);
    try {
      await api.uzytkownicy({ ...zapytanie, odswiez: 'tak' });
    } catch {
      // celowo puste: stan błędu pokaże `lista` po unieważnieniu niżej
    } finally {
      setOdswiezanie(false);
    }
    await poZapisie();
  }

  const dane: UzytkownicyOdpowiedz | undefined = lista.data;
  const sort = (zapytanie.sort ?? 'utworzono') as SortowanieKont;
  const kierunek = zapytanie.kierunek ?? 'malejaco';

  const naglowek = (
    <NaglowekEkranu
      tytul="Użytkownicy"
      opis={
        <>
          Konta z systemu głównego — ta sama lista co „Konta” w starym panelu. Filtry i sumy liczą CAŁY zbiór;
          doładowanie konta klienta oraz usunięcie konta klienta albo restauratora idą tymi samymi operacjami,
          z hasłem operatora.
        </>
      }
      kartaId="ADMIN-USERS-00"
    />
  );

  if (lista.isLoading && !dane) {
    return (
      <Stack spacing={2}>
        {naglowek}
        <LoadingState label="Wczytywanie kont z systemu głównego… (pierwszy odczyt pełnej listy może potrwać do minuty)" />
      </Stack>
    );
  }
  if (lista.error && !dane) {
    return (
      <Stack spacing={2}>
        {naglowek}
        <ErrorState
          error={lista.error}
          context={[powodBleduOdczytu(lista.error), 'Lista kont czyta się wyłącznie z systemu głównego — bez kopii zastępczej.'].filter(Boolean).join(' ')}
        />
        <Box><Button onClick={() => void lista.refetch()}>Spróbuj ponownie</Button></Box>
      </Stack>
    );
  }
  if (!dane) return naglowek;

  return (
    <Stack spacing={2.5}>
      {naglowek}

      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 2 }}>
        {([
          ['Ilość klientów', formatCount(dane.podsumowanie.liczbaKlientow)],
          ['Suma portfeli', formatGrosze(dane.podsumowanie.sumaPortfeliGrosze)],
          ['Wszystkich kont', formatCount(dane.wszystkich)],
        ] as const).map(([etykieta, wartosc]) => (
          <Paper key={etykieta} variant="outlined" sx={{ px: 2, py: 1.25, minWidth: 150 }} data-podsumowanie={etykieta}>
            <Typography className="sygnal-liczba" sx={{ fontSize: 24, fontWeight: 800, lineHeight: 1.1 }}>{wartosc}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{etykieta}</Typography>
          </Paper>
        ))}
      </Stack>

      <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 1.5, alignItems: 'center' }}>
        <TextField
          size="small"
          label="Szukaj: imię, e-mail, telefon, szkoła, miasto, ID"
          value={szukaj}
          onChange={(e) => setSzukaj(e.target.value)}
          slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
          sx={{ minWidth: { xs: '100%', sm: 360 } }}
        />
        <Button size="small" onClick={() => setZaawansowane((v) => !v)} aria-expanded={zaawansowane}>
          {zaawansowane ? 'Ukryj filtry zaawansowane' : 'Filtry zaawansowane'}
        </Button>
        {filtrAktywny ? <Button size="small" onClick={wyczysc}>Wyczyść filtry</Button> : null}
        <Box sx={{ flexGrow: 1 }} />
        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
          Odczytano {new Date(dane.pobrano).toLocaleTimeString('pl-PL', { timeZone: 'Europe/Warsaw' })}
        </Typography>
        <Button size="small" onClick={() => void odswiez()} disabled={lista.isFetching || odswiezanie}>
          {odswiezanie ? 'Odświeżanie…' : 'Odśwież'}
        </Button>
      </Stack>

      <Collapse in={zaawansowane} unmountOnExit>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Box sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' } }}>
            {([['imie', 'Imię'], ['email', 'E-mail'], ['telefon', 'Telefon']] as const).map(([k, etykieta]) => (
              <FiltrTekstowy key={k} etykieta={etykieta} wartosc={frazy[k] ?? ''} onZmiana={(v) => ustawFraze(k, v)} />
            ))}
            <TextField select size="small" label="Rola" value={parametry.get('rola') ?? ''} onChange={(e) => ustaw({ rola: e.target.value || null })}>
              <MenuItem value="">Wszystkie</MenuItem>
              {dane.slowniki.role.map((r: RolaKonta) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </TextField>
            <TextField
              select size="small" label="Miasto" value={parametry.get('miasto') ?? ''}
              onChange={(e) => ustaw({ miasto: e.target.value || null, szkola: null })}
            >
              <MenuItem value="">Wszystkie</MenuItem>
              {dane.slowniki.miasta.map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Szkoła" value={parametry.get('szkola') ?? ''} onChange={(e) => ustaw({ szkola: e.target.value || null })}>
              <MenuItem value="">Wszystkie</MenuItem>
              {dane.slowniki.szkoly.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Potwierdzony" value={parametry.get('potwierdzony') ?? ''} onChange={(e) => ustaw({ potwierdzony: e.target.value || null })}>
              <MenuItem value="">Wszystkie</MenuItem>
              <MenuItem value="tak">Potwierdzony</MenuItem>
              <MenuItem value="nie">Niepotwierdzony</MenuItem>
            </TextField>
            <TextField select size="small" label="Zablokowany" value={parametry.get('zablokowany') ?? ''} onChange={(e) => ustaw({ zablokowany: e.target.value || null })}>
              <MenuItem value="">Wszystkie</MenuItem>
              <MenuItem value="tak">Zablokowany</MenuItem>
              <MenuItem value="nie">Niezablokowany</MenuItem>
            </TextField>
          </Box>
        </Paper>
      </Collapse>

      <Typography variant="body2" role="status" aria-live="polite" data-wynikow={dane.wynikow}>
        {filtrAktywny
          ? <>Wyników: <strong>{formatCount(dane.wynikow)}</strong> z {formatCount(dane.wszystkich)} kont (filtr na całym zbiorze).</>
          : <>Kont: <strong>{formatCount(dane.wszystkich)}</strong>.</>}
        {lista.isFetching ? ' Odświeżanie…' : ''}
      </Typography>

      {lista.error ? (
        <Alert severity="error" role="alert">
          Nie udało się odświeżyć listy — pokazano ostatni odczyt. {powodBleduOdczytu(lista.error) ?? ''}
        </Alert>
      ) : null}

      {dane.konta.length === 0 ? (
        <EmptyState
          title={filtrAktywny ? 'Brak kont spełniających filtry' : 'Brak kont'}
          description={filtrAktywny ? 'Filtry przeszukały cały zbiór. Zmień je albo wyczyść.' : 'System główny nie zwrócił żadnego konta.'}
        />
      ) : (
        <TableContainer component={Paper} variant="outlined" sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="Konta użytkowników">
            <TableHead>
              <TableRow>
                {KOLUMNY.map((kol) => (
                  <TableCell key={kol.klucz} align={kol.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
                    sortDirection={kol.sort && sort === kol.sort ? (kierunek === 'rosnaco' ? 'asc' : 'desc') : false}>
                    {kol.sort ? (
                      <TableSortLabel
                        active={sort === kol.sort}
                        direction={sort === kol.sort && kierunek === 'rosnaco' ? 'asc' : 'desc'}
                        onClick={() => ustaw({
                          sort: kol.sort!,
                          kierunek: sort === kol.sort && kierunek === 'malejaco' ? 'rosnaco' : null,
                        })}
                      >
                        {kol.etykieta}
                      </TableSortLabel>
                    ) : kol.etykieta}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {dane.konta.map((k) => (
                <TableRow key={`${k.id}-${k.rola}`} hover data-konto-id={k.id}>
                  {KOLUMNY.map((kol) => (kol.klucz === 'akcje' ? (
                    <TableCell key="akcje" align="right" sx={{ whiteSpace: 'nowrap' }}>
                      {k.rola === 'Klient' && mozeDoladowac ? (
                        <Button size="small" onClick={() => setDoladowanie(k)} aria-label={`Doładuj ${k.email ?? k.id}`}>Doładuj</Button>
                      ) : null}
                      {(k.rola === 'Klient' || k.rola === 'Restaurator') && mozeUsunac ? (
                        <Button size="small" color="error" onClick={() => setUsuniecie(k)}
                          aria-label={`Usuń konto ${k.rola === 'Restaurator' ? 'restauratora' : 'klienta'} ${k.email ?? k.id}`}>Usuń</Button>
                      ) : null}
                    </TableCell>
                  ) : (
                    <TableCell key={kol.klucz} align={kol.align}
                      sx={{ whiteSpace: kol.klucz === 'email' || kol.klucz === 'szkola' ? 'normal' : 'nowrap', maxWidth: 260 }}>
                      {kol.klucz === 'zablokowany' && k.zablokowany ? <Chip size="small" color="warning" label="tak" />
                        : kol.klucz === 'potwierdzony' && !k.potwierdzony ? <Chip size="small" variant="outlined" label="nie" />
                          : wartoscKomorki(k, kol.klucz)}
                    </TableCell>
                  )))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={dane.wynikow}
            page={dane.strona - 1}
            rowsPerPage={dane.rozmiar}
            rowsPerPageOptions={[...ROZMIARY]}
            onPageChange={(_, p) => ustaw({ strona: p > 0 ? String(p + 1) : null }, true)}
            onRowsPerPageChange={(e) => ustaw({ rozmiar: e.target.value === '25' ? null : e.target.value })}
            labelRowsPerPage="Wierszy na stronę"
            labelDisplayedRows={({ from, to, count }) => `${from}–${to} z ${count}`}
          />
        </TableContainer>
      )}

      {!mozeDoladowac && !mozeUsunac ? (
        <Alert severity="info">Twoje konto widzi listę kont, ale nie doładowuje ich ani nie usuwa.</Alert>
      ) : null}

      <Box>
        <Button size="small" onClick={() => setPokazAudyt((v) => !v)}>
          {pokazAudyt ? 'Ukryj ostatnie operacje' : 'Pokaż ostatnie operacje na kontach'}
        </Button>
        <Collapse in={pokazAudyt} unmountOnExit>
          <Paper variant="outlined" sx={{ p: 2, mt: 1, overflowX: 'auto' }}>
            {audyt.data?.wpisy.length ? (
              <Table size="small" aria-label="Ostatnie operacje na kontach">
                <TableHead>
                  <TableRow>
                    <TableCell>Kiedy</TableCell><TableCell>Kto</TableCell><TableCell>Operacja</TableCell>
                    <TableCell>Konto</TableCell><TableCell>Przed</TableCell><TableCell>Po</TableCell><TableCell>Wynik</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {audyt.data.wpisy.map((w, i) => (
                    <TableRow key={`${w.czas}-${i}`}>
                      <TableCell>{new Date(w.czas).toLocaleString('pl-PL', { timeZone: 'Europe/Warsaw' })}</TableCell>
                      <TableCell>{w.aktor.email ?? '—'}</TableCell>
                      <TableCell>
                        {w.operacja === 'DOLADOWANIE'
                          ? `Doładowanie ${formatGrosze(Number(w.encja.kwotaGrosze))}`
                          : `Usunięcie konta${w.encja.rola ? ` (${String(w.encja.rola)})` : ''}`}
                      </TableCell>
                      <TableCell sx={{ wordBreak: 'break-all' }}>{String(w.encja.konto ?? '—')}</TableCell>
                      <TableCell>{opisStanu(w.przed)}</TableCell>
                      <TableCell>{opisStanu(w.po)}</TableCell>
                      <TableCell>{w.wynik}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : audyt.isLoading ? <LoadingState /> : (
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Brak operacji od uruchomienia usługi.</Typography>
            )}
            <Typography variant="caption" component="div" sx={{ color: 'text.secondary', mt: 1 }}>
              Trwały ślad każdej operacji jest w dzienniku serwera Control Center (bez haseł); tu — ostatnie wpisy od uruchomienia usługi.
            </Typography>
          </Paper>
        </Collapse>
      </Box>

      {doladowanie ? (
        <OknoDoladowania konto={doladowanie} loginDomyslny={loginOperatora} onZamknij={() => setDoladowanie(null)} onPoZapisie={poZapisie} />
      ) : null}
      {usuniecie ? (
        <OknoUsuniecia konto={usuniecie} loginDomyslny={loginOperatora} onZamknij={() => setUsuniecie(null)} onPoZapisie={poZapisie} />
      ) : null}
    </Stack>
  );
}

function opisStanu(s: unknown): string {
  if (s === null || s === undefined) return '—';
  if (typeof s === 'object') {
    const o = s as Record<string, unknown>;
    if (typeof o.saldoGrosze === 'number') return formatGrosze(o.saldoGrosze);
    if (o.naLiscie === false) return 'konta brak na liście';
    if (o.naLiscie === true) return 'konto na liście';
  }
  return JSON.stringify(s);
}

/**
 * Pole filtra zaawansowanego: zatwierdzane przy wyjściu z pola albo klawiszem Enter. Niekontrolowane,
 * z kluczem = wartość z adresu — „Wyczyść filtry" czyści je bez lustrzanego stanu.
 */
function FiltrTekstowy({ etykieta, wartosc, onZmiana }: { etykieta: string; wartosc: string; onZmiana: (v: string) => void }) {
  const zatwierdz = (v: string) => { if (v.trim() !== wartosc) onZmiana(v.trim()); };
  return (
    <TextField
      key={wartosc}
      size="small"
      label={etykieta}
      defaultValue={wartosc}
      onBlur={(e) => zatwierdz(e.target.value)}
      onKeyDown={(e) => { if (e.key === 'Enter') zatwierdz((e.target as HTMLInputElement).value); }}
      helperText="Enter albo wyjście z pola"
    />
  );
}

export default UzytkownicyPage;
